// @ts-strict-ignore
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ProjectHeadVO, ProjectOptionDefVO, ProjectOptionLocalizationVO, ProjectOptionVO, ProjectVO } from '@project/model/vo/project.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { UserVO } from '@user/model/vo/user.vo';
import { IntRangeVO } from '../../article/model/vo/property.vo';
import { StringUtils } from '@shared/util/string-utils';
import { UserUtils } from '@user/service/user-utils';
import { CustomValidators } from '@shared/custom-validators';
import { ValidatorFn, Validators } from '@angular/forms';
import { ProjectOptionType, ProjectOptionWrapper } from '@project/components/project/util/project-option-data';
import { NumberUtils } from '@shared/util/number-utils';
import { ProjectOptionDefIds } from '@project/components/project/constants/project-option-def-ids';
import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ProjectOptionUtils {
  public static readonly MAX_PROJECT_OPTION_VALUE_LENGTH: number = 255;
  public static readonly MAX_PROJECT_NAME_LENGTH: number = 500;

  public static readonly VALID_RANGE_OF_PROJECT_OPTIONS: IntRangeVO[] = [new IntRangeVO(0, 80), new IntRangeVO(81, 145), new IntRangeVO(146, 149)];

  private static readonly GENERAL_OPTION_RANGES: IntRangeVO[] = [new IntRangeVO(1, 80), new IntRangeVO(146, 149)];
  private static readonly FSF_OPTION_RANGE: IntRangeVO = new IntRangeVO(81, 145);
  private static readonly INTERNAL_OPTION_RANGE: IntRangeVO = new IntRangeVO(150, 179);

  constructor(
    private _localeResourceManager: LocaleResourceManager,
    private localeResourceManager: LocaleResourceManager,
  ) {}

  public static findProjectOptionByIdentifier(projectOptions: ProjectOptionVO[], identifier: string): ProjectOptionVO {
    if (ArrayUtils.isEmpty(projectOptions) || !identifier) {
      return null;
    }
    for (const option of projectOptions) {
      if (option.identifier === identifier) {
        return option;
      }
    }
    return null;
  }

  public static removeProjectOptionByIdentifier(projectOptions: ProjectOptionVO[], identifier: string): void {
    if (!projectOptions || !identifier) {
      return;
    }
    for (let i: number = 0; i < projectOptions.length; i++) {
      const option: ProjectOptionVO = projectOptions[i];
      if (option.identifier === identifier) {
        projectOptions.splice(i, 1);
        return;
      }
    }
  }

  public static replaceOptionsValue(options: ProjectOptionVO[], identifier: string, optionValue: string): void {
    const option: ProjectOptionVO = this.findProjectOptionByIdentifier(options, identifier);
    if (option) {
      option.value = optionValue;
    } else {
      const projectOption: ProjectOptionVO = new ProjectOptionVO();
      projectOption.identifier = identifier;
      projectOption.value = optionValue;
      options.push(projectOption);
    }
  }

  public static findProjectOptionDefByIdentifier(projectOptionDefs: ProjectOptionDefVO[], identifier: string): ProjectOptionDefVO {
    if (!projectOptionDefs || !identifier) {
      return null;
    }
    for (const projectOptionDef of projectOptionDefs) {
      if (projectOptionDef.identifier === identifier) {
        return projectOptionDef;
      }
    }
    return null;
  }

  public static getProjectOptionDefMaxLengthByIdentifier(projectOptionDefs: ProjectOptionDefVO[], identifier: string): number {
    return this.findProjectOptionDefByIdentifier(projectOptionDefs, identifier).maxLength;
  }

  public static getUserProfileOptionValueByIdentifier(vo: UserVO, identifierChain: string, localeResourceManager: LocaleResourceManager): string {
    if (!vo || !identifierChain) {
      return null;
    }
    const tokens: string[] = identifierChain.split('.');
    let host: any = vo;
    for (const token of tokens) {
      if (host && token in host) {
        host = host[token];
      } else {
        return null;
      }
    }
    if (typeof host === 'string') {
      if (tokens.length > 0 && tokens[tokens.length-1] === 'salutation') {
        return localeResourceManager.getResolvedString(host);
      } else {
        return host;
      }
    } else {
      return null;
    }
  }

  public static convertProjectOptionDefToProjectOption(optionDef: ProjectOptionDefVO): ProjectOptionVO {
    if (!optionDef) {
      return null;
    }
    const projectOption: ProjectOptionVO = new ProjectOptionVO();
    projectOption.identifier = optionDef.identifier;
    projectOption.order = optionDef.order;
    projectOption.localizations = [];
    for (const localDef of optionDef.localizations) {
      const projectOptionLocalization: ProjectOptionLocalizationVO = new ProjectOptionLocalizationVO();
      projectOptionLocalization.locale = localDef.locale;
      projectOptionLocalization.value = localDef.value;
      projectOption.localizations.push(projectOptionLocalization);
    }
    return projectOption;
  }

  private static isValidProjectOptionIdentifier(identifier: string): boolean {
    for (const validIdentifierRange of ProjectOptionUtils.VALID_RANGE_OF_PROJECT_OPTIONS) {
      if (validIdentifierRange.contains(Number(identifier))) {
        return true;
      }
    }
    return false;
  }

  public static updateLimitationOptionsToDefault(
    project: ProjectVO,
    projectOptions: ProjectOptionVO[],
    projectOptionDefs: ProjectOptionDefVO[]
  ): void {
    for (const optionProject of projectOptions.concat()) {
      if (!ProjectOptionUtils.isValidProjectOptionIdentifier(optionProject.identifier)) {
        projectOptions.splice(projectOptions.indexOf(optionProject), 1);
      }
    }
    for (const optionDef of projectOptionDefs) {
      if (optionDef && optionDef.defaultValue) {
        if (!ProjectOptionUtils.isValidProjectOptionIdentifier(optionDef.identifier)) {
          const projectOption: ProjectOptionVO = this.convertProjectOptionDefToProjectOption(optionDef);
          projectOption.value = optionDef.defaultValue;
          projectOptions.push(projectOption);
          if (optionDef.identifier === ProjectOptionDefIds.PROJECT_VERSION) {
            // update current version display string on project.
            // This property exists, so version can be retrieved in cases where project options are not transmitted.
            project.currentVersionDisplayString = projectOption.value;
          }
        }
      }
    }
  }

  public static setDefaultProjectOptionValues(
    project: ProjectVO,
    projectOptionDefs: ProjectOptionDefVO[],
    overwriteExistingValues: boolean = false
  ): void {
    if (!project) {
      return;
    }
    if (!ArrayUtils.isEmpty(projectOptionDefs)) {
      if (!project.options) {
        project.options = [];
      }
      for (const optionDef of projectOptionDefs) {
        if (!StringUtils.isEmpty(optionDef.defaultValue)) {
          // in case we overwrite existing values, it also means overwriting exiting labels (definition) for that project option.
          // Otherwise overwritten value could not make sense for previously existing label
          if (overwriteExistingValues) {
            this.removeProjectOptionByIdentifier(project.options, optionDef.identifier);
          }
          let projectOption: ProjectOptionVO = overwriteExistingValues
            ? null
            : this.findProjectOptionByIdentifier(project.options, optionDef.identifier);
          if (!projectOption) {
            projectOption = this.convertProjectOptionDefToProjectOption(optionDef);
            project.options.push(projectOption);
          }
          if (StringUtils.isEmpty(projectOption.value) || overwriteExistingValues) {
            projectOption.value = optionDef.defaultValue;
            if (optionDef.identifier === ProjectOptionDefIds.PROJECT_VERSION) {
              // update current version display string on project.
              // This property exists, so version can be retrieved in cases where project options are not transmitted.
              project.currentVersionDisplayString = projectOption.value;
            }
          }
        }
      }
    }
  }

  public static convertProjectOptionDefsToProjectOptions(projectOptionDefs: ProjectOptionDefVO[]): ProjectOptionVO[] {
    const convertedProjectOptions: any[] = [];
    for (const projectDef of projectOptionDefs) {
      const convertedProjectOptionDef: ProjectOptionVO = this.convertProjectOptionDefToProjectOption(projectDef);
      convertedProjectOptions.push(convertedProjectOptionDef);
    }
    return convertedProjectOptions;
  }

  public createWrapperByProjectOption(
    projectOption: ProjectOptionVO,
    required: boolean,
    isMultiLine: boolean,
    maxLength: number = null
  ): ProjectOptionWrapper {
    const wrappedProjectOption: ProjectOptionWrapper = new ProjectOptionWrapper();
    wrappedProjectOption.displayName = this.getProjectOptionDisplayName(projectOption);
    wrappedProjectOption.projectOption = projectOption;
    wrappedProjectOption.required = required;
    wrappedProjectOption.optionType = isMultiLine ? ProjectOptionType.MultilineText : ProjectOptionType.SimpleText;
    wrappedProjectOption.visible = true;
    wrappedProjectOption.validator = this.createProjectOptionValidator(required, maxLength);
    return wrappedProjectOption;
  }

  public getProjectOptionDisplayName(projectOption: ProjectOptionVO | ProjectOptionDefVO, locale: string = null): string {
    if (!projectOption) {
      return null;
    }
    if (ProjectOptionUtils.isProjectOptionWithFixedName(projectOption.identifier)) {
      return this.getProjectOptionFixedName(projectOption.identifier);
    } else {
      return locale
        ? this.projectOptionLocalizationValue(projectOption.localizations, locale)
        : this.projectOptionLocalizationValueByLocaleChain(projectOption.localizations);
    }
  }

  private getProjectOptionFixedName(identifierString: string): string {
    return this._localeResourceManager.getValueByKey('projectOption.name.' + identifierString);
  }

  private createProjectOptionValidator(required: boolean, maxLength: number): ValidatorFn {
    const maxAllowedLength: number = ProjectOptionUtils.getMaxAllowedValueLength(maxLength);
    return required
      ? Validators.compose([CustomValidators.trimRequired, CustomValidators.maxLength(maxAllowedLength)])
      : CustomValidators.maxLength(maxAllowedLength);
  }

  public static getMaxAllowedValueLength(optionDefMaxLength: number): number {
    return NumberUtils.isNumber(optionDefMaxLength) && optionDefMaxLength > 0
      ? Math.min(optionDefMaxLength, ProjectOptionUtils.MAX_PROJECT_OPTION_VALUE_LENGTH)
      : ProjectOptionUtils.MAX_PROJECT_OPTION_VALUE_LENGTH;
  }

  public static canSeeInternalOptions(user: UserVO): boolean {
    return user && (user.isAdmin() || user.isClerk());
  }

  public static isForcedInvisible(identifier: string, user: UserVO, finalisationView: boolean = false): boolean {
    if (finalisationView || !UserUtils.isWagoUser(user)) {
      if (ProjectOptionDefIds.SPECIAL_PROJECT_OPTIONS.indexOf(identifier) > -1) {
        return true;
      }
    }
    return false;
  }

  public static isProjectVersionOption(identifierString: string): boolean {
    if (!identifierString) {
      return false;
    }
    return identifierString === ProjectOptionDefIds.PROJECT_VERSION;
  }

  public static isGeneralOption(identifierString: string): boolean {
    if (!identifierString) {
      return false;
    }

    const identifier: number = parseInt(identifierString, 10);
    for (const range of ProjectOptionUtils.GENERAL_OPTION_RANGES) {
      if (range.contains(identifier)) {
        return true;
      }
    }
    return false;
  }

  public static isFSFOption(identifierString: string): boolean {
    if (!identifierString) {
      return false;
    }

    const identifier: number = parseInt(identifierString, 10);
    return ProjectOptionUtils.FSF_OPTION_RANGE.contains(identifier);
  }

  public static isInternalOption(identifierString: string): boolean {
    if (!identifierString) {
      return false;
    }

    const identifier: number = parseInt(identifierString, 10);
    return ProjectOptionUtils.INTERNAL_OPTION_RANGE.contains(identifier);
  }

  public static isProjectSettingsAvailable(project: ProjectHeadVO, user: UserVO): boolean {
    if (!project || !user) {
      return false;
    }
    if (project.isArticleProject || !project.projectGroup) {
      return false;
    }
    return (
      user.hasPermission("LOAD_PROJECT_OPTION_DEFINITIONS") &&
      (user.hasPermission("VIEW_PROJECT_OPTIONS") || user.hasPermission("EDIT_PROJECT_OPTIONS"))
    );
  }

  public static sortProjectOptionDefsByOrder(projectOptionDefs: ProjectOptionDefVO[]): void {
    if (!projectOptionDefs) {
      return;
    }
    projectOptionDefs.sort((o1, o2) => NumberUtils.numericCompare(o1.order, o2.order));
  }

  public static getCleanedProjectOptionValue(value: string): string {
    if (!value) {
      return null;
    }
    value = value.replace('\r', '');
    return value.replace('\n', ' / ');
  }

  public static trimValueByMaxLength(value: string, maxLength: number): string {
    if (!maxLength) {
      return value;
    }
    if (!value) {
      return '';
    }
    return value.substring(0, maxLength);
  }

  public static isProjectOptionWithFixedName(identifierString: string): boolean {
    return this.isFSFOption(identifierString) || this.isProjectVersionOption(identifierString);
  }

  public static getValueFromProjectOptions(projectOptions: ProjectOptionVO[], identifier: string): string {
    const projectOption: ProjectOptionVO = this.findProjectOptionByIdentifier(projectOptions, identifier);
    if (projectOption) {
      return projectOption.value;
    } else {
      return '';
    }
  }

  static removeByIdentifier(projectOptions: ProjectOptionVO[], identifier: string) {
    const projectOption: ProjectOptionVO = this.findProjectOptionByIdentifier(projectOptions, identifier);
    if (projectOption) {
      projectOptions.splice(projectOptions.indexOf(projectOption), 1);
    }
    return projectOption;
  }

  public projectOptionLocalizationValue(projectOptionLocalizations: ProjectOptionLocalizationVO[], locale: string = this.localeResourceManager.currentLanguage.langKey): string | undefined {
    if (ArrayUtils.isEmpty(projectOptionLocalizations)) {
      return undefined;
    }
    for (const projectOptionLocalization of projectOptionLocalizations) {
      if (projectOptionLocalization.locale === locale || projectOptionLocalization.locale === null) {
        return projectOptionLocalization.value;
      }
    }
    return undefined;
  }

  public projectOptionLocalizationValueByLocaleChain(
    projectOptionLocalizations: ProjectOptionLocalizationVO[],
  ): string | undefined {
    if (ArrayUtils.isEmpty(projectOptionLocalizations)) {
      return undefined;
    }
    if (ArrayUtils.isEmpty(this.localeResourceManager.getLocaleChain())) {
      return this.projectOptionLocalizationValue(projectOptionLocalizations);
    } else {
      for (const locale of this.localeResourceManager.getLocaleChain()) {
        const value = this.projectOptionLocalizationValue(projectOptionLocalizations, locale);
        if (!StringUtils.isEmpty(value)) {
          return value;
        }
      }
      return undefined;
    }
  }
}
